import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, LinearProgress, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import { PageHeader, TxHashLink, shortAddr, AddressWithCopy } from '../components/common';
import { VerifiedBadge } from './MyPage';

const ALL_ROLES = ['user', 'issuer_st', 'issuer_sc', 'admin'] as const;

interface AdminUser {
  userUid: string;
  loginId: string;
  status: string;
  countryCode: string;
  createdAt: string;
  /** 마지막 로그인 시각. null = 로그인 기록 없음(컬럼 추가 이전 로그인 포함) */
  lastLoginAt: string | null;
  roles: string[];
  wallet: string | null;
  /** 활성 지갑의 키 유형 (TYPE1 사용자 직접 관리 / TYPE2 서비스 위탁 관리) */
  keyType: 'TYPE1' | 'TYPE2' | null;
  verified: boolean | null;
  /** 컨트랙트 권한(owner/agent) — 온체인 조회. null = 지갑 없음 */
  onchain?: { level: 'owner' | 'agent' | 'user' | null; contracts: string[] } | null;
  /** 스테이블코인 동결 여부. null = 체인 조회 실패(동결 아님과 구분한다) */
  frozen?: boolean | null;
}
interface RoleHistory {
  id: string;
  userUid: string;
  before: string | null;
  after: string;
  changedBy: string;
  onchainTx: string | null;
  changedAt: string;
}

export function AdminRoles() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get<AdminUser[]>('/admin/users') });
  const history = useQuery({ queryKey: ['role-history'], queryFn: () => api.get<RoleHistory[]>('/admin/roles/history') });
  // 사용자 필터: 아이디 · 역할 · 키 유형 · 온체인 권한
  const [q, setQ] = useState('');
  const [roleF, setRoleF] = useState('');
  const [keyF, setKeyF] = useState('');
  const [chainF, setChainF] = useState('');
  const userRows = useMemo(() => (users.data ?? []).filter((u) => {
    const t = q.trim().toLowerCase();
    if (t && !`${u.loginId} ${u.userUid} ${u.wallet ?? ''}`.toLowerCase().includes(t)) return false;
    if (roleF && !(roleF === '-' ? u.roles.length === 0 : u.roles.includes(roleF))) return false;
    if (keyF && (u.keyType ?? '-') !== keyF) return false;
    if (chainF && (u.onchain?.level ?? '-') !== chainF) return false;
    return true;
  }), [users.data, q, roleF, keyF, chainF]);

  const mutate = useMutation({
    mutationFn: (v: { userUid: string; role: string; action: 'grant' | 'revoke' }) =>
      api.post(`/admin/roles/${v.userUid}`, { role: v.role, action: v.action }),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['role-history'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : '권한 변경 실패'),
  });

  return (
    <Box>
      <PageHeader
        crumb="관리자 · 권한"
        title="권한 관리"
        desc="사용자에게 역할을 부여/회수합니다. admin 역할 부여로 관리자를 추가할 수 있습니다."
        action={<Button component={Link} to="/admin/keys" variant="outlined">키 관리 (교체 · 폐기) →</Button>}
      />
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h2" mb={1.5}>사용자 · 역할</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField size="small" label="아이디 · uid · 지갑" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 220 }} />
            <TextField select size="small" label="역할" value={roleF} onChange={(e) => setRoleF(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">전체</MenuItem>
              {ALL_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              <MenuItem value="-">역할 없음</MenuItem>
            </TextField>
            <TextField select size="small" label="키 유형" value={keyF} onChange={(e) => setKeyF(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="TYPE1">Type1 (직접 관리)</MenuItem>
              <MenuItem value="TYPE2">Type2 (위탁 관리)</MenuItem>
              <MenuItem value="-">지갑 없음</MenuItem>
            </TextField>
            <TextField select size="small" label="온체인 권한" value={chainF} onChange={(e) => setChainF(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="owner">owner</MenuItem>
              <MenuItem value="agent">agent</MenuItem>
              <MenuItem value="user">일반</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">{userRows.length} / {users.data?.length ?? 0}명</Typography>
          </Stack>
        </Box>
        {users.isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>아이디</TableCell><TableCell>상태</TableCell>
              <TableCell>가입일 · 마지막 로그인</TableCell>
              <TableCell>지갑 상태 · 키 유형</TableCell>
              <TableCell>역할 · 온체인 권한</TableCell><TableCell width={220}>역할 부여</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userRows.map((u) => (
              <UserRow key={u.userUid} u={u} busy={mutate.isPending}
                onChange={(role, action) => mutate.mutate({ userUid: u.userUid, role, action })} />
            ))}
            {users.data && userRows.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                {users.data.length === 0 ? '사용자가 없습니다.' : '조건에 맞는 사용자가 없습니다.'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper>
        <Box sx={{ p: 2 }}><Typography variant="h2">권한 변경 이력</Typography></Box>
        {history.isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>대상(uid)</TableCell><TableCell>변경</TableCell><TableCell>변경자</TableCell>
              <TableCell>온체인 tx</TableCell><TableCell>시점</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(history.data ?? []).map((h) => (
              <TableRow key={h.id} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{shortAddr(h.userUid)}</TableCell>
                <TableCell>{h.before ? `${h.before} → ` : ''}{describeChange(h.after)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{shortAddr(h.changedBy)}</TableCell>
                <TableCell><TxHashLink hash={h.onchainTx} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{new Date(h.changedAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {history.data?.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>이력이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

function UserRow({ u, busy, onChange }: {
  u: AdminUser;
  busy: boolean;
  onChange: (role: string, action: 'grant' | 'revoke') => void;
}) {
  const grantable = ALL_ROLES.filter((r) => !u.roles.includes(r));
  const [sel, setSel] = useState<string>('');
  return (
    <TableRow hover>
      <TableCell>
        <Stack>
          <Typography fontWeight={600}>{u.loginId}</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{shortAddr(u.userUid)}</Typography>
        </Stack>
      </TableCell>
      <TableCell><Chip size="small" label={u.status} color={u.status === 'ACTIVE' ? 'success' : 'default'} /></TableCell>
      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        <div>{new Date(u.createdAt).toLocaleDateString('ko-KR')}</div>
        <Typography variant="caption" color="text.secondary">
          {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ko-KR') : '로그인 기록 없음'}
        </Typography>
      </TableCell>
      <TableCell>
        {u.wallet ? (
          <Stack spacing={0.3}>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <VerifiedBadge verified={u.verified} />
              {/* 동결은 isVerified 와 별개다 — 동결된 계정도 "검증됨"으로 보이므로 따로 표시한다 */}
              {u.frozen === true && <Chip size="small" color="error" label="동결(KRWS)" sx={{ fontWeight: 700 }} />}
              {u.frozen === null && <Chip size="small" label="동결 확인 불가" sx={{ fontWeight: 700 }} />}
              {u.keyType && (
                <Chip size="small" variant="outlined" label={u.keyType === 'TYPE1' ? 'Type1 직접' : 'Type2 위탁'} />
              )}
            </Stack>
            <AddressWithCopy address={u.wallet} />
          </Stack>
        ) : <Typography variant="caption" color="text.secondary">지갑 없음</Typography>}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {u.roles.length === 0 && <Typography color="text.secondary">—</Typography>}
          {u.roles.map((r) => (
            <Chip key={r} size="small" label={r} color={r === 'admin' ? 'warning' : 'default'}
              onDelete={busy ? undefined : () => onChange(r, 'revoke')} />
          ))}
        </Stack>
        {/* 서비스 역할(DB)과 별개로, 컨트랙트에 실제로 박힌 권한을 보여준다 */}
        {u.onchain && (u.onchain.level === 'owner' || u.onchain.level === 'agent') && (
          <Typography variant="caption" color="text.secondary" component="div" mt={0.5}>
            온체인 {u.onchain.level}: {u.onchain.contracts.join(', ')}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Select size="small" value={sel} displayEmpty disabled={busy || grantable.length === 0}
            onChange={(e) => setSel(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="" disabled>역할 선택</MenuItem>
            {grantable.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
          <Button size="small" variant="outlined" disabled={busy || !sel}
            onClick={() => { onChange(sel, 'grant'); setSel(''); }}>부여</Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

/**
 * 이력 문구 해석.
 * 이 표에는 역할 변경뿐 아니라 **키 생애주기**(교체·폐기)도 같은 사용자 축으로 쌓인다
 * (RFP: "키 폐기 또는 재발급, 교체 시 사용자와의 이력 연계가 가능해야 함").
 * 원문 그대로 두면 `key:rotate:0x…` 처럼 읽히지 않으므로 사람이 읽을 형태로 바꾼다.
 */
function describeChange(after: string): string {
  const rotate = /^key:rotate:(0x[0-9a-fA-F]{40})$/.exec(after);
  if (rotate) return `키 교체 → 새 지갑 ${shortAddr(rotate[1])}`;
  const revoke = /^key:revoke:(0x[0-9a-fA-F]{40})$/.exec(after);
  if (revoke) return `키 폐기 — ${shortAddr(revoke[1])}`;
  const keytype = /^keytype:requested=(\w+):issued=(\w+)$/.exec(after);
  if (keytype) return `키 유형 변경 발급 — 신청 ${keytype[1]} → 발급 ${keytype[2]}`;
  if (after === 'password-reset (script)') return '비밀번호 재설정(관리자 스크립트)';
  const grant = /^(grant|revoke):(.+)$/.exec(after);
  if (grant) return `${grant[1] === 'grant' ? '역할 부여' : '역할 회수'} — ${grant[2]}`;
  return after;
}
